import { type ReactNode } from 'react';
import { z } from 'zod';
import {
    BarChartSchema,
    DonutChartSchema,
    LineChartSchema,
    type ChartSpec,
    type NumberFormat,
} from './chart-spec.js';

// Re-exported so the blog's one-line wrapper and Meridian's viewer can pull the
// spec and the renderer from the same module.
export { ChartSpecSchema } from './chart-spec.js';
export type { ChartSpec } from './chart-spec.js';

/**
 * Declarative charts rendered as static, server-safe SVG.
 *
 * ONE chart engine, two callers. The website blog embeds charts with a fenced
 * ```chart JSON block (see `apps/website/src/lib/blog-charts.tsx`, which wraps
 * this module); Meridian emits `kind: 'chart'` artifacts whose content is the
 * same JSON (see `packages/@west-tennessee-consulting/meridian/src/lib/artifacts`). Both validate
 * against {@link ChartSpecSchema}, so a spec that renders in one renders in the
 * other and there is no second chart implementation to drift.
 *
 * Deliberately dependency-free: inline SVG, no charting library, no client JS.
 * That is what lets the same component render inside static generation on the
 * website and inside a chat message in Hub. Colors come from CSS variables, so
 * charts inherit each surface's theme (including dark mode) automatically.
 *
 * This module renders a VALIDATED spec. Callers parse untrusted JSON with
 * {@link ChartSpecSchema} first and handle the failure in their own idiom —
 * a build error for the blog, an error card in chat.
 */

/** Brand-first palette; series without an explicit color cycle through this. */
const PALETTE = ['var(--primary)', 'var(--secondary)', '#16a34a', '#f59e0b', '#7c3aed'] as const;

const paletteColor = (index: number, explicit?: string): string =>
    explicit ?? PALETTE[index % PALETTE.length] ?? PALETTE[0];

const formatValue = (value: number, format: NumberFormat): string => {
    switch (format) {
        case 'usd':
            return `$${Math.round(value).toLocaleString('en-US')}`;
        case 'pct':
            return `${value}%`;
        default:
            return value.toLocaleString('en-US');
    }
};

/** "Nice" upper bound + step so the y axis lands on round numbers. */
const niceScale = (max: number, ticks = 4): { top: number; step: number } => {
    if (max <= 0) return { top: 1, step: 1 };
    const roughStep = max / ticks;
    const magnitude = 10 ** Math.floor(Math.log10(roughStep));
    const normalized = roughStep / magnitude;
    const niceStep = (normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10) * magnitude;
    return { top: Math.ceil(max / niceStep) * niceStep, step: niceStep };
};

// Shared geometry for the axis charts.
const W = 720;
const H = 420;
const M = { top: 48, right: 24, bottom: 64, left: 72 } as const;
const PLOT_W = W - M.left - M.right;
const PLOT_H = H - M.top - M.bottom;

const Legend = ({
    items
}: {
    items: readonly { name: string; color: string }[];
}): ReactNode => (
    <div className='mt-3 flex flex-wrap justify-center gap-x-5 gap-y-1 text-sm text-muted-foreground'>
        {items.map((item) => (
            <span key={item.name} className='inline-flex items-center gap-2'>
                <span
                    aria-hidden='true'
                    className='inline-block size-3 rounded-sm'
                    style={{ backgroundColor: item.color }}
                />
                {item.name}
            </span>
        ))}
    </div>
);

const Figure = ({
    title,
    caption,
    children
}: {
    title?: string;
    caption?: string;
    children: ReactNode;
}): ReactNode => (
    <figure className='my-8 rounded-2xl border border-border bg-card p-4 sm:p-6'>
        {title !== undefined && (
            <figcaption className='mb-4 text-center text-base font-semibold text-foreground'>
                {title}
            </figcaption>
        )}
        {children}
        {caption !== undefined && (
            <p className='mt-3 text-center text-xs text-muted-foreground'>{caption}</p>
        )}
    </figure>
);

const yGrid = (top: number, step: number, format: NumberFormat): ReactNode[] => {
    const lines: ReactNode[] = [];
    for (let value = 0; value <= top + 1e-9; value += step) {
        const y = M.top + PLOT_H - (value / top) * PLOT_H;
        lines.push(
            <g key={value}>
                <line
                    x1={M.left}
                    x2={M.left + PLOT_W}
                    y1={y}
                    y2={y}
                    stroke='var(--border)'
                    strokeWidth={1}
                />
                <text
                    x={M.left - 10}
                    y={y}
                    textAnchor='end'
                    dominantBaseline='middle'
                    fontSize={12}
                    fill='var(--muted-foreground)'
                >
                    {formatValue(value, format)}
                </text>
            </g>
        );
    }
    return lines;
};

const renderBar = (spec: z.infer<typeof BarChartSchema>): ReactNode => {
    const max = Math.max(...spec.series.flatMap((s) => s.values), 0);
    const { top, step } = niceScale(max);
    const groups = spec.categories.length;
    const groupW = PLOT_W / groups;
    const seriesCount = spec.series.length;
    const barGap = 0.18 * (groupW / seriesCount);
    const barW = (groupW * 0.72) / seriesCount;

    return (
        <Figure title={spec.title} caption={spec.caption}>
            <svg
                viewBox={`0 0 ${W} ${H}`}
                width='100%'
                role='img'
                aria-label={spec.title ?? 'Bar chart'}
                style={{ height: 'auto' }}
            >
                {spec.yLabel !== undefined && (
                    <text
                        transform={`translate(18 ${M.top + PLOT_H / 2}) rotate(-90)`}
                        textAnchor='middle'
                        fontSize={12}
                        fill='var(--muted-foreground)'
                    >
                        {spec.yLabel}
                    </text>
                )}
                {yGrid(top, step, spec.valueFormat)}
                {spec.categories.map((cat, gi) => {
                    const groupX = M.left + gi * groupW;
                    return (
                        <g key={cat}>
                            {spec.series.map((s, si) => {
                                const value = s.values[gi] ?? 0;
                                const barH = (value / top) * PLOT_H;
                                const x = groupX + (groupW - seriesCount * barW) / 2 + si * barW + barGap / 2;
                                const y = M.top + PLOT_H - barH;
                                return (
                                    <g key={s.name}>
                                        <rect
                                            x={x}
                                            y={y}
                                            width={barW - barGap}
                                            height={barH}
                                            rx={4}
                                            fill={paletteColor(si, s.color)}
                                        />
                                        {spec.showValues && (
                                            <text
                                                x={x + (barW - barGap) / 2}
                                                y={y - 6}
                                                textAnchor='middle'
                                                fontSize={12}
                                                fontWeight={600}
                                                fill='var(--foreground)'
                                            >
                                                {formatValue(value, spec.valueFormat)}
                                            </text>
                                        )}
                                    </g>
                                );
                            })}
                            <text
                                x={groupX + groupW / 2}
                                y={M.top + PLOT_H + 22}
                                textAnchor='middle'
                                fontSize={13}
                                fill='var(--foreground)'
                            >
                                {cat}
                            </text>
                        </g>
                    );
                })}
                <line
                    x1={M.left}
                    x2={M.left + PLOT_W}
                    y1={M.top + PLOT_H}
                    y2={M.top + PLOT_H}
                    stroke='var(--foreground)'
                    strokeWidth={1.5}
                />
            </svg>
            {seriesCount > 1 && (
                <Legend
                    items={spec.series.map((s, si) => ({
                        name: s.name,
                        color: paletteColor(si, s.color)
                    }))}
                />
            )}
        </Figure>
    );
};

const renderLine = (spec: z.infer<typeof LineChartSchema>): ReactNode => {
    const max = Math.max(...spec.series.flatMap((s) => s.values), 0);
    const min = Math.min(...spec.series.flatMap((s) => s.values), 0);
    const { top, step } = niceScale(max);
    const points = spec.categories.length;
    const xAt = (i: number): number =>
        points === 1 ? M.left + PLOT_W / 2 : M.left + (i / (points - 1)) * PLOT_W;
    const yAt = (v: number): number => M.top + PLOT_H - (v / top) * PLOT_H;

    return (
        <Figure title={spec.title} caption={spec.caption}>
            <svg
                viewBox={`0 0 ${W} ${H}`}
                width='100%'
                role='img'
                aria-label={spec.title ?? 'Line chart'}
                style={{ height: 'auto' }}
            >
                {spec.yLabel !== undefined && (
                    <text
                        transform={`translate(18 ${M.top + PLOT_H / 2}) rotate(-90)`}
                        textAnchor='middle'
                        fontSize={12}
                        fill='var(--muted-foreground)'
                    >
                        {spec.yLabel}
                    </text>
                )}
                {yGrid(top, step, spec.valueFormat)}
                {min < 0 && (
                    <line
                        x1={M.left}
                        x2={M.left + PLOT_W}
                        y1={yAt(0)}
                        y2={yAt(0)}
                        stroke='var(--foreground)'
                        strokeWidth={1.5}
                    />
                )}
                {spec.series.map((s, si) => {
                    const color = paletteColor(si, s.color);
                    const path = s.values
                        .map((v, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(v)}`)
                        .join(' ');
                    return (
                        <g key={s.name}>
                            <path d={path} fill='none' stroke={color} strokeWidth={2.5} />
                            {s.values.map((v, i) => (
                                <g key={spec.categories[i] ?? i}>
                                    <circle cx={xAt(i)} cy={yAt(v)} r={4} fill={color} />
                                    {spec.showValues && (
                                        <text
                                            x={xAt(i)}
                                            y={yAt(v) - 10}
                                            textAnchor='middle'
                                            fontSize={12}
                                            fontWeight={600}
                                            fill='var(--foreground)'
                                        >
                                            {formatValue(v, spec.valueFormat)}
                                        </text>
                                    )}
                                </g>
                            ))}
                        </g>
                    );
                })}
                {spec.categories.map((cat, i) => (
                    <text
                        key={cat}
                        x={xAt(i)}
                        y={M.top + PLOT_H + 24}
                        textAnchor='middle'
                        fontSize={13}
                        fill='var(--foreground)'
                    >
                        {cat}
                    </text>
                ))}
            </svg>
            {spec.series.length > 1 && (
                <Legend
                    items={spec.series.map((s, si) => ({
                        name: s.name,
                        color: paletteColor(si, s.color)
                    }))}
                />
            )}
        </Figure>
    );
};

const polarToCartesian = (cx: number, cy: number, r: number, angle: number): [number, number] => [
    cx + r * Math.cos(angle - Math.PI / 2),
    cy + r * Math.sin(angle - Math.PI / 2)
];

const renderDonut = (spec: z.infer<typeof DonutChartSchema>): ReactNode => {
    const total = spec.segments.reduce((sum, seg) => sum + seg.value, 0);
    const size = 320;
    const cx = size / 2;
    const cy = size / 2;
    const rOuter = 130;
    const rInner = 78;
    let angle = 0;

    return (
        <Figure title={spec.title} caption={spec.caption}>
            <div className='flex justify-center'>
                <svg
                    viewBox={`0 0 ${size} ${size}`}
                    width='100%'
                    style={{ maxWidth: 320, height: 'auto' }}
                    role='img'
                    aria-label={spec.title ?? 'Donut chart'}
                >
                    {total === 0 ? (
                        <circle cx={cx} cy={cy} r={(rOuter + rInner) / 2} fill='none' stroke='var(--border)' strokeWidth={rOuter - rInner} />
                    ) : (
                        spec.segments.map((seg, i) => {
                            const fraction = seg.value / total;
                            const start = angle;
                            const end = angle + fraction * 2 * Math.PI;
                            angle = end;
                            const largeArc = end - start > Math.PI ? 1 : 0;
                            const [x1, y1] = polarToCartesian(cx, cy, rOuter, start);
                            const [x2, y2] = polarToCartesian(cx, cy, rOuter, end);
                            const [x3, y3] = polarToCartesian(cx, cy, rInner, end);
                            const [x4, y4] = polarToCartesian(cx, cy, rInner, start);
                            const d = [
                                `M ${x1} ${y1}`,
                                `A ${rOuter} ${rOuter} 0 ${largeArc} 1 ${x2} ${y2}`,
                                `L ${x3} ${y3}`,
                                `A ${rInner} ${rInner} 0 ${largeArc} 0 ${x4} ${y4}`,
                                'Z'
                            ].join(' ');
                            return <path key={seg.label} d={d} fill={paletteColor(i, seg.color)} />;
                        })
                    )}
                    {spec.centerLabel !== undefined && (
                        <text
                            x={cx}
                            y={cy}
                            textAnchor='middle'
                            dominantBaseline='middle'
                            fontSize={22}
                            fontWeight={700}
                            fill='var(--foreground)'
                        >
                            {spec.centerLabel}
                        </text>
                    )}
                </svg>
            </div>
            <Legend
                items={spec.segments.map((seg, i) => ({
                    name: `${seg.label} — ${formatValue(seg.value, spec.valueFormat)}`,
                    color: paletteColor(i, seg.color)
                }))}
            />
        </Figure>
    );
};

/** Render a validated chart spec to a server-side React SVG figure. */
export const renderChart = (spec: ChartSpec): ReactNode => {
    switch (spec.type) {
        case 'bar':
            return renderBar(spec);
        case 'line':
            return renderLine(spec);
        case 'donut':
            return renderDonut(spec);
    }
};
